import { useEffect, useState } from "react";
import { API_ERROR_MESSAGES } from "../constants/api";
import ActionPageLayout from "../layouts/ActionPageLayout";
import usePageTitle from "../hooks/usePageTitle";
import useScrollToTopOnMessage from "../hooks/useScrollToTopOnMessage";
import {
  assignInboundUrgent,
  fetchInboundHistory,
  fetchInboundMetadata,
  saveInboundEntry,
} from "../services/inboundApi";
import { fetchApplicationByLeadId } from "../services/applicationApi";
import {
  FaAddressCard,
  FaBolt,
  FaCalculator,
  FaCheckSquare,
  FaClipboardList,
  FaFolderOpen,
  FaRegCommentDots,
  FaSave,
  FaSearch,
  FaUserTie,
  FaUndoAlt,
} from "react-icons/fa";
import { MdCallReceived } from "react-icons/md";

const inputClass =
  "h-[38px] w-full rounded-md border border-[#d8dee6] bg-white px-3 text-base outline-none transition focus:border-[#0d73ff] focus:shadow-[0_0_0_4px_rgba(13,115,255,0.18)]";

const emptyMetadata = {
  options: {
    cities: [],
    cityStateMap: {},
    contactSources: [],
    customerPriorities: [],
    contactCategories: [],
  },
  defaults: {
    contactCategory: "",
  },
};

const emptyFormValues = {
  name: "",
  house: "",
  street: "",
  locality: "",
  pinCode: "",
  comments: "",
};

function fieldValue(value) {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value);
}

function getApiErrorMessage(error) {
  const errors = error.details?.errors;

  if (errors && typeof errors === "object") {
    const firstError = Object.values(errors).find(Boolean);

    if (firstError) {
      return firstError;
    }
  }

  return error.message;
}

function summaryValue(value) {
  return fieldValue(value) || "N/A";
}

function firstApplicationValue(...values) {
  for (const value of values) {
    const normalizedValue = fieldValue(value).trim();

    if (normalizedValue) {
      return normalizedValue;
    }
  }

  return "";
}

function formatApplicationLabel(value) {
  const rawValue = firstApplicationValue(value);

  if (!rawValue) {
    return "";
  }

  return rawValue
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function objectValue(source, camelKey, snakeKey) {
  const value = source?.[camelKey] || source?.[snakeKey];

  return value && typeof value === "object" && !Array.isArray(value)
    ? value
    : {};
}

function getApplicationLookupData(applicationLookup) {
  const summary = applicationLookup?.summary || {};
  const application = {
    ...summary,
    ...(applicationLookup?.application || {}),
  };

  return {
    application,
    applicationId: firstApplicationValue(
      applicationLookup?.applicationId,
      application.id,
      application.applicationId,
      application.application_id,
    ),
  };
}

function mapInboundApplicationInfo(applicationLookup, fallbackInfo = {}) {
  if (!applicationLookup) {
    return fallbackInfo;
  }

  const { application, applicationId } =
    getApplicationLookupData(applicationLookup);
  const loanApprovalDetails = objectValue(
    application,
    "loanApprovalDetails",
    "loan_approval_details",
  );
  const rejectionDetails = objectValue(
    application,
    "rejectionDetails",
    "rejection_details",
  );
  const loanProduct = formatApplicationLabel(
    firstApplicationValue(application.productType, application.product_type),
  );
  const taskType = formatApplicationLabel(
    firstApplicationValue(application.taskType, application.task_type),
  );
  const status = formatApplicationLabel(
    firstApplicationValue(
      application.taskStatus,
      application.task_status,
      application.state,
      application.status,
    ),
  );
  const assigned = firstApplicationValue(
    application.assignedTo,
    application.assigned_to,
    loanApprovalDetails.approvedBy,
    loanApprovalDetails.approved_by,
    application.createdBy,
    application.created_by,
  );
  const appStatus = formatApplicationLabel(
    firstApplicationValue(application.state, application.status),
  );
  const rejectReason = firstApplicationValue(
    rejectionDetails.message,
    rejectionDetails.reason,
    rejectionDetails.rejectionReason,
    rejectionDetails.rejection_reason,
    rejectionDetails.remarks,
  );

  return {
    ...fallbackInfo,
    loanProduct: summaryValue(loanProduct || fallbackInfo.loanProduct),
    taskType: summaryValue(taskType || fallbackInfo.taskType),
    status: summaryValue(status || fallbackInfo.status),
    assigned: summaryValue(assigned || fallbackInfo.assigned),
    appId: summaryValue(applicationId || fallbackInfo.appId),
    appStatus: summaryValue(appStatus || fallbackInfo.appStatus),
    rejectReason: summaryValue(rejectReason || fallbackInfo.rejectReason),
  };
}

function urgentAssignmentMessage(response) {
  if (response.message) {
    return response.message;
  }

  return response.assignedTo
    ? `Contact assigned urgently to ${response.assignedTo}.`
    : "Contact assigned urgently.";
}

function getStateForCity(city, metadata) {
  if (!city) {
    return "Other";
  }

  return metadata.options.cityStateMap[city] || "Other";
}

function mapInboundHistory(response, applicationLookup = null) {
  const history = response.history || {};
  const contactInfo = history.contactInfo || {};
  const address = history.address || {};
  const documents = history.documents || {};
  const communication = history.communication || {};
  const application = history.application || {};
  const applicationInfo = mapInboundApplicationInfo(applicationLookup, {
    loanProduct: summaryValue(contactInfo.loanProduct),
    taskType: summaryValue(application.taskType),
    status: summaryValue(application.taskStatus),
    assigned: summaryValue(application.taskAssignedTo),
    appId: summaryValue(application.applicationId),
    appStatus: summaryValue(application.status),
    rejectReason: summaryValue(application.rejectionReason),
  });

  return {
    form: {
      name: fieldValue(contactInfo.name),
      house: fieldValue(address.house),
      street: fieldValue(address.street),
      locality: fieldValue(address.locality),
      pinCode: "",
      comments: fieldValue(communication.comments),
    },
    fields: {
      phone: fieldValue(contactInfo.phone),
      city: fieldValue(contactInfo.city),
      contactSource: fieldValue(contactInfo.contactSource),
      contactCategory: fieldValue(contactInfo.contactCategory),
    },
    summary: {
      personal: {
        name: summaryValue(contactInfo.name),
        phone: summaryValue(contactInfo.phone),
        city: summaryValue(contactInfo.city),
        house: summaryValue(address.house),
        street: summaryValue(address.street),
        locality: summaryValue(address.locality),
      },
      document: {
        panPresent: summaryValue(documents.panPresent),
        panNumber: summaryValue(documents.panNumber),
        idProof: summaryValue(documents.idProof),
        dob: summaryValue(documents.dob),
      },
      communication: {
        category: summaryValue(contactInfo.contactCategory),
        contactSource: summaryValue(contactInfo.contactSource),
        sourceDesc: summaryValue(contactInfo.contactSourceDescription),
        lastCalled: summaryValue(communication.lastCalledOn),
        status: summaryValue(communication.lastCallStatus),
        subStatus: summaryValue(communication.subStatus),
        assignedTo: summaryValue(communication.assignedTo),
        comments: summaryValue(
          communication.comments || communication.whatsAppLog,
        ),
      },
      application: applicationInfo,
    },
  };
}

function Field({ label, required = false, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1.5 block text-base">
        {label}
        {required ? " *" : ""}
      </span>
      {children}
    </label>
  );
}

function SelectField({
  label,
  required = false,
  value = "",
  options = [],
  placeholder,
  disabled = false,
  onChange,
}) {
  const placeholderText = placeholder || `-- Select ${label} --`;
  const visibleOptions =
    value && !options.includes(value) ? [value, ...options] : options;

  return (
    <Field label={label} required={required}>
      <select
        className={inputClass}
        disabled={disabled}
        value={value}
        onChange={onChange}
      >
        <option value="">{placeholderText}</option>
        {visibleOptions.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </Field>
  );
}

function TextField({
  label,
  required = false,
  defaultValue = "",
  placeholder,
  value,
  readOnly = false,
  onChange,
}) {
  const valueProps =
    value === undefined ? { defaultValue } : { value, onChange };

  return (
    <Field label={label} required={required}>
      <input
        className={inputClass}
        placeholder={placeholder || `Enter ${label.toLowerCase()}`}
        readOnly={readOnly}
        type="text"
        {...valueProps}
      />
    </Field>
  );
}

function Panel({
  icon,
  title,
  titleClassName = "text-[#0d73ff]",
  children,
  className = "",
}) {
  return (
    <section
      className={`rounded-[0.5rem] border border-[#d1d5db] bg-white p-4 ${className}`}
    >
      <h2
        className={`mb-5 flex items-center gap-2 text-base font-semibold ${titleClassName}`}
      >
        {icon}
        {title}
      </h2>
      {children}
    </section>
  );
}

function HistoryField({ label, value }) {
  return (
    <div>
      <dt className="mb-1 text-base font-bold">{label}</dt>
      <dd className="m-0 whitespace-pre-line break-words text-base leading-[1.45]">
        {value}
      </dd>
    </div>
  );
}

function HistoryPanel({ icon, title, titleClassName, children }) {
  return (
    <section className="rounded-[0.5rem] border border-[#d1d5db] bg-white px-4 py-5">
      <h2
        className={`mb-5 flex items-center gap-2 text-base font-semibold ${titleClassName}`}
      >
        {icon}
        {title}
      </h2>
      {children}
    </section>
  );
}

function HistorySummary({ history }) {
  const { personal, document, communication, application } = history;

  return (
    <div className="mb-10 grid grid-cols-3 gap-4 max-[1100px]:grid-cols-1">
      <HistoryPanel
        icon={
          <FaUserTie
            className="h-4 w-4 flex-none text-[#e9a03d]"
            aria-hidden="true"
          />
        }
        title="Personal Info"
        titleClassName="text-[#0d73ff]"
      >
        <dl className="grid grid-cols-3 gap-x-5 gap-y-4 max-[720px]:grid-cols-1">
          <HistoryField label="Name:" value={personal.name} />
          <HistoryField label="Phone:" value={personal.phone} />
          <HistoryField label="City:" value={personal.city} />
          <HistoryField label="House:" value={personal.house} />
          <HistoryField label="Street:" value={personal.street} />
          <HistoryField label="Locality:" value={personal.locality} />
        </dl>

        <div className="my-5 h-px bg-[#e5e7eb]" />

        <h3 className="mb-4 flex items-center gap-2 text-base font-semibold text-[#ffb000]">
          <FaFolderOpen className="h-4 w-4 flex-none" aria-hidden="true" />
          Document Info
        </h3>
        <dl className="grid grid-cols-2 gap-x-7 gap-y-4 max-[720px]:grid-cols-1">
          <HistoryField label="PAN Present:" value={document.panPresent} />
          <HistoryField label="PAN Number:" value={document.panNumber} />
          <HistoryField label="ID Proof:" value={document.idProof} />
          <HistoryField label="DOB:" value={document.dob} />
        </dl>
      </HistoryPanel>

      <HistoryPanel
        icon={
          <MdCallReceived
            className="h-4 w-4 flex-none text-[#d63384]"
            aria-hidden="true"
          />
        }
        title={`Communication Info ( Category : ${communication.category} )`}
        titleClassName="text-[#16834a]"
      >
        <dl className="grid grid-cols-2 gap-x-7 gap-y-4 max-[720px]:grid-cols-1">
          <HistoryField
            label="Contact Source:"
            value={communication.contactSource}
          />
          <HistoryField label="Source Desc:" value={communication.sourceDesc} />
          <HistoryField label="Last Called:" value={communication.lastCalled} />
          <HistoryField label="Status:" value={communication.status} />
          <HistoryField label="Sub-Status:" value={communication.subStatus} />
          <HistoryField label="Assigned To:" value={communication.assignedTo} />
          <div className="col-span-2 max-[720px]:col-span-1">
            <HistoryField label="Comments:" value={communication.comments} />
          </div>
        </dl>
      </HistoryPanel>

      <HistoryPanel
        icon={
          <FaClipboardList
            className="h-4 w-4 flex-none text-[#d58b63]"
            aria-hidden="true"
          />
        }
        title="Application Info"
        titleClassName="text-[#dc3545]"
      >
        <dl className="grid grid-cols-3 gap-x-7 gap-y-4 max-[900px]:grid-cols-2 max-[720px]:grid-cols-1">
          <div className="col-span-3 max-[900px]:col-span-2 max-[720px]:col-span-1">
            <HistoryField
              label="Loan Product:"
              value={application.loanProduct}
            />
          </div>
          <HistoryField label="Task Type:" value={application.taskType} />
          <HistoryField label="Status:" value={application.status} />
          <HistoryField label="Assigned:" value={application.assigned} />
          <HistoryField label="App ID:" value={application.appId} />
          <HistoryField label="App Status:" value={application.appStatus} />
          <div className="col-span-3 max-[900px]:col-span-2 max-[720px]:col-span-1">
            <HistoryField
              label="Reject Reason:"
              value={application.rejectReason}
            />
          </div>
        </dl>
      </HistoryPanel>
    </div>
  );
}

function InboundCallEntryPage() {
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [entrySaved, setEntrySaved] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [saveError, setSaveError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [formValues, setFormValues] = useState(emptyFormValues);
  const [historySummary, setHistorySummary] = useState(null);
  const [showAssignButton, setShowAssignButton] = useState(false);
  const [urgentMessage, setUrgentMessage] = useState("");
  const [urgentError, setUrgentError] = useState("");
  const [isAssigningUrgent, setIsAssigningUrgent] = useState(false);
  const [historyMessage, setHistoryMessage] = useState("");
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [metadata, setMetadata] = useState(emptyMetadata);
  const [metadataError, setMetadataError] = useState("");
  const [isMetadataLoading, setIsMetadataLoading] = useState(true);
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedState, setSelectedState] = useState("Other");
  const [selectedContactSource, setSelectedContactSource] = useState("");
  const [selectedPriority, setSelectedPriority] = useState("");
  const [selectedContactCategory, setSelectedContactCategory] = useState("");

  usePageTitle("Inbound Call Entry | Pargro");
  useScrollToTopOnMessage(
    [
      entrySaved ? saveMessage || "Entry saved successfully!" : "",
      urgentMessage,
      saveError,
      urgentError,
      metadataError,
      historyMessage,
    ]
      .filter(Boolean)
      .join("|"),
  );

  useEffect(() => {
    let shouldIgnore = false;

    async function loadMetadata() {
      try {
        const inboundMetadata = await fetchInboundMetadata();

        if (shouldIgnore) {
          return;
        }

        setMetadata(inboundMetadata);
        setSelectedContactCategory(
          (currentValue) =>
            currentValue || inboundMetadata.defaults?.contactCategory || "",
        );
      } catch (error) {
        if (!shouldIgnore) {
          setMetadataError(error.message);
        }
      } finally {
        if (!shouldIgnore) {
          setIsMetadataLoading(false);
        }
      }
    }

    loadMetadata();

    return () => {
      shouldIgnore = true;
    };
  }, []);

  useEffect(() => {
    if (selectedCity) {
      setSelectedState(getStateForCity(selectedCity, metadata));
    }
  }, [metadata, selectedCity]);

  const resetMetadataFields = () => {
    setSelectedCity("");
    setSelectedState("Other");
    setSelectedContactSource("");
    setSelectedPriority("");
    setSelectedContactCategory(metadata.defaults.contactCategory || "");
  };

  const updateFormValue = (fieldName) => (event) => {
    setFormValues((currentValues) => ({
      ...currentValues,
      [fieldName]: event.target.value,
    }));
  };

  const handleCheckHistory = async () => {
    const requestedPhoneNumber = phoneNumber.trim();

    if (!requestedPhoneNumber) {
      setHistoryMessage("Please enter a phone number to check history.");
      return;
    }

    setEntrySaved(false);
    setSaveMessage("");
    setSaveError("");
    setUrgentMessage("");
    setUrgentError("");
    setHistoryMessage("");
    setIsHistoryLoading(true);

    try {
      const [historyLookup, applicationLookup] = await Promise.all([
        fetchInboundHistory(requestedPhoneNumber)
          .then((historyResponse) => ({ historyResponse }))
          .catch((historyError) => ({ historyError })),
        fetchApplicationByLeadId(requestedPhoneNumber)
          .then((applicationData) => ({ applicationData }))
          .catch((applicationError) => ({ applicationError })),
      ]);

      if (historyLookup.historyError && applicationLookup.applicationError) {
        throw historyLookup.historyError;
      }

      const response =
        historyLookup.historyResponse ||
        (applicationLookup.applicationData
          ? {
              found: true,
              history: {
                contactInfo: {
                  phone: requestedPhoneNumber,
                },
              },
              showAssignButton: false,
            }
          : null);

      if (!response?.found && !applicationLookup.applicationData) {
        setHistoryLoaded(false);
        setHistorySummary(null);
        setShowAssignButton(false);
        setFormValues(emptyFormValues);
        resetMetadataFields();
        setHistoryMessage(response?.message || "No contact history found.");
        return;
      }

      const mappedHistory = mapInboundHistory(
        response?.found
          ? response
          : {
              found: true,
              history: {
                contactInfo: {
                  phone: requestedPhoneNumber,
                },
              },
              showAssignButton: false,
            },
        applicationLookup.applicationData || null,
      );
      const nextCity = mappedHistory.fields.city;

      setPhoneNumber(mappedHistory.fields.phone || requestedPhoneNumber);
      setFormValues(mappedHistory.form);
      setSelectedCity(nextCity);
      setSelectedState(getStateForCity(nextCity, metadata));
      setSelectedContactSource(mappedHistory.fields.contactSource);
      setSelectedContactCategory(
        mappedHistory.fields.contactCategory ||
          metadata.defaults.contactCategory ||
          "",
      );
      setHistorySummary(mappedHistory.summary);
      setShowAssignButton(Boolean(response.showAssignButton));
      setHistoryLoaded(true);
      setHistoryMessage(
        historyLookup.historyError
          ? `History unavailable: ${historyLookup.historyError.message}`
          : "",
      );
    } catch (error) {
      setHistoryLoaded(false);
      setHistorySummary(null);
      setShowAssignButton(false);
      setFormValues(emptyFormValues);
      resetMetadataFields();
      setHistoryMessage(error.message);
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const handleReset = () => {
    setPhoneNumber("");
    setHistoryLoaded(false);
    setEntrySaved(false);
    setSaveMessage("");
    setSaveError("");
    setUrgentMessage("");
    setUrgentError("");
    setFormValues(emptyFormValues);
    setHistorySummary(null);
    setShowAssignButton(false);
    setHistoryMessage("");
    resetMetadataFields();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setEntrySaved(false);
    setSaveMessage("");
    setSaveError("");
    setUrgentMessage("");
    setUrgentError("");
    setIsSaving(true);

    try {
      const response = await saveInboundEntry({
        phone_number: phoneNumber.trim(),
        name: formValues.name.trim(),
        city: selectedCity,
        customer_priority: selectedPriority,
        house_building_number: formValues.house.trim(),
        street_name: formValues.street.trim(),
        locality_area: formValues.locality.trim(),
        pincode: formValues.pinCode.trim(),
        comments: formValues.comments.trim(),
        contact_source: selectedContactSource,
        contact_source_description: "",
        contact_category: selectedContactCategory,
      });

      if (response.success && response.saved) {
        setEntrySaved(true);
        setSaveMessage(response.message || "Entry saved successfully!");
        setPhoneNumber("");
        setHistoryLoaded(false);
        setFormValues(emptyFormValues);
        setHistorySummary(null);
        setShowAssignButton(false);
        setHistoryMessage("");
        resetMetadataFields();
        return;
      }

      setSaveError(
        response.message || API_ERROR_MESSAGES.inboundEntrySaveFailed,
      );
    } catch (error) {
      setSaveError(getApiErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCityChange = (event) => {
    const city = event.target.value;

    setSelectedCity(city);
    setSelectedState(
      city ? metadata.options.cityStateMap[city] || "" : "Other",
    );
  };

  const handleAssignUrgent = async () => {
    const requestedPhoneNumber = phoneNumber.trim();
    const customerName = formValues.name.trim();

    setEntrySaved(false);
    setSaveMessage("");
    setSaveError("");
    setUrgentMessage("");
    setUrgentError("");
    setHistoryMessage("");

    if (!requestedPhoneNumber) {
      setUrgentError("Please enter a phone number before assigning urgently.");
      return;
    }

    if (!customerName) {
      setUrgentError(
        "Please enter the customer name before assigning urgently.",
      );
      return;
    }

    setIsAssigningUrgent(true);

    try {
      const response = await assignInboundUrgent({
        phoneNumber: requestedPhoneNumber,
        name: customerName,
      });

      setUrgentMessage(urgentAssignmentMessage(response));
      setSelectedPriority("URGENT");
      setShowAssignButton(false);

      if (response.assignedTo) {
        setHistorySummary((currentSummary) => {
          if (!currentSummary) {
            return currentSummary;
          }

          return {
            ...currentSummary,
            communication: {
              ...currentSummary.communication,
              assignedTo: response.assignedTo,
            },
            application: {
              ...currentSummary.application,
              assigned: response.assignedTo,
            },
          };
        });
      }
    } catch (error) {
      setUrgentError(getApiErrorMessage(error));
    } finally {
      setIsAssigningUrgent(false);
    }
  };

  return (
    <ActionPageLayout
      title="Inbound Call Entry"
      icon={
        <MdCallReceived
          className="h-6 w-6 flex-none text-[#6d4b8a]"
          aria-hidden="true"
        />
      }
    >
      {entrySaved && (
        <div
          className="mt-6 flex items-center gap-3 rounded-md border border-[#9dd4b6] bg-[#d8efe3] px-5 py-5 text-base text-[#064e3b]"
          role="status"
        >
          <FaCheckSquare
            className="h-5 w-5 flex-none text-[#43c77a]"
            aria-hidden="true"
          />
          {saveMessage || "Entry saved successfully!"}
        </div>
      )}

      {saveError && (
        <div
          className="mt-6 rounded-md border border-[#f5c2c7] bg-[#f8d7da] px-4 py-3 text-sm text-[#842029]"
          role="alert"
        >
          {saveError}
        </div>
      )}

      {urgentMessage && (
        <div
          className="mt-6 flex items-center gap-3 rounded-md border border-[#9dd4b6] bg-[#d8efe3] px-5 py-5 text-base text-[#064e3b]"
          role="status"
        >
          <FaCheckSquare
            className="h-5 w-5 flex-none text-[#43c77a]"
            aria-hidden="true"
          />
          {urgentMessage}
        </div>
      )}

      {urgentError && (
        <div
          className="mt-6 rounded-md border border-[#f5c2c7] bg-[#f8d7da] px-4 py-3 text-sm text-[#842029]"
          role="alert"
        >
          {urgentError}
        </div>
      )}

      {metadataError && (
        <div
          className="mt-6 rounded-md border border-[#f5c2c7] bg-[#f8d7da] px-4 py-3 text-sm text-[#842029]"
          role="alert"
        >
          {metadataError}
        </div>
      )}

      {historyMessage && (
        <div
          className="mt-6 rounded-md border border-[#f5c2c7] bg-[#f8d7da] px-4 py-3 text-sm text-[#842029]"
          role="alert"
        >
          {historyMessage}
        </div>
      )}

      <form
        className="mt-6 border border-[#d9dfe8] bg-[#f8fafc] px-4 py-5 shadow-[0_2px_8px_rgba(15,23,42,0.12)]"
        key={historyLoaded ? "history-loaded" : "history-empty"}
        onSubmit={handleSubmit}
        onReset={handleReset}
      >
        <div
          className={`${historyLoaded ? "mb-3" : "mb-10"} grid grid-cols-[33.125rem_minmax(13rem,1fr)_minmax(13rem,1fr)] items-end gap-6 max-[980px]:grid-cols-1`}
        >
          <Field label="Phone Number" required>
            <input
              className={inputClass}
              placeholder="Enter caller phone number"
              type="tel"
              inputMode="numeric"
              maxLength={10}
              value={phoneNumber}
              onChange={(event) => setPhoneNumber(event.target.value)}
            />
          </Field>

          <button
            className="inline-flex h-[38px] items-center justify-center gap-2 rounded-md bg-[#6c757d] px-6 text-base font-medium text-white transition hover:bg-[#5c656d] focus-visible:bg-[#5c656d] disabled:cursor-not-allowed disabled:bg-[#9aa2a8]"
            disabled={isHistoryLoading}
            type="button"
            onClick={handleCheckHistory}
          >
            <FaSearch className="h-4 w-4 flex-none" aria-hidden="true" />
            {isHistoryLoading ? "Checking History..." : "Check History"}
          </button>

          <button
            className="inline-flex h-[38px] items-center justify-center gap-2 rounded-md border border-[#dc3545] bg-white px-6 text-base font-medium text-[#dc3545] transition hover:bg-[#fff5f5] focus-visible:bg-[#fff5f5]"
            type="reset"
          >
            <FaUndoAlt className="h-4 w-4 flex-none" aria-hidden="true" />
            Reset
          </button>
        </div>

        {historyLoaded && (
          <>
            {showAssignButton && (
              <button
                className="mb-3 inline-flex h-[38px] items-center justify-center gap-2 rounded-md bg-[#ffc107] px-5 text-base font-medium text-[#111827] transition hover:bg-[#f0b400] focus-visible:bg-[#f0b400] disabled:cursor-not-allowed disabled:bg-[#ffe08a]"
                disabled={isAssigningUrgent}
                type="button"
                onClick={handleAssignUrgent}
              >
                <FaBolt
                  className="h-4 w-4 flex-none text-[#ff7a00]"
                  aria-hidden="true"
                />
                {isAssigningUrgent ? "Assigning..." : "Assign as URGENT"}
              </button>
            )}
            {historySummary && <HistorySummary history={historySummary} />}
          </>
        )}

        <div className="grid grid-cols-[minmax(18rem,0.95fr)_minmax(24rem,1.95fr)] gap-4 max-[980px]:grid-cols-1">
          <Panel
            icon={
              <FaCalculator className="h-4 w-4 flex-none" aria-hidden="true" />
            }
            title="Contact Info"
          >
            <div className="grid gap-4">
              <TextField
                label="Name"
                required
                placeholder="Enter customer name"
                value={formValues.name}
                onChange={updateFormValue("name")}
              />
              <SelectField
                label="Contact Source"
                required
                disabled={isMetadataLoading}
                options={metadata.options.contactSources}
                placeholder={
                  isMetadataLoading
                    ? "-- Loading contact sources --"
                    : "-- Select contact source --"
                }
                value={selectedContactSource}
                onChange={(event) =>
                  setSelectedContactSource(event.target.value)
                }
              />
              <SelectField
                label="Priority"
                disabled={isMetadataLoading}
                options={metadata.options.customerPriorities}
                placeholder="-- Select priority --"
                value={selectedPriority}
                onChange={(event) => setSelectedPriority(event.target.value)}
              />
              <SelectField
                label="Contact Category"
                disabled={isMetadataLoading}
                options={metadata.options.contactCategories}
                placeholder={
                  isMetadataLoading
                    ? "-- Loading categories --"
                    : "-- Select category --"
                }
                value={selectedContactCategory}
                onChange={(event) =>
                  setSelectedContactCategory(event.target.value)
                }
              />
            </div>
          </Panel>

          <Panel
            icon={
              <FaAddressCard className="h-4 w-4 flex-none" aria-hidden="true" />
            }
            title="Address Info"
            titleClassName="text-[#4b5563]"
          >
            <div className="grid grid-cols-2 gap-x-6 gap-y-4 max-[760px]:grid-cols-1">
              <SelectField
                label="City"
                required
                disabled={isMetadataLoading}
                options={metadata.options.cities}
                placeholder={
                  isMetadataLoading
                    ? "-- Loading cities --"
                    : "-- Select city --"
                }
                value={selectedCity}
                onChange={handleCityChange}
              />
              <TextField
                label="State"
                placeholder="Auto-filled from city"
                readOnly
                value={selectedState}
                onChange={(event) => setSelectedState(event.target.value)}
              />
              <TextField
                label="House / Building No."
                placeholder="Enter house or building no."
                value={formValues.house}
                onChange={updateFormValue("house")}
              />
              <TextField
                label="Street Name"
                placeholder="Enter street name"
                value={formValues.street}
                onChange={updateFormValue("street")}
              />
              <TextField
                label="Locality / Area"
                placeholder="Enter locality or area"
                value={formValues.locality}
                onChange={updateFormValue("locality")}
              />
              <TextField
                label="PIN Code"
                placeholder="Enter PIN code"
                value={formValues.pinCode}
                onChange={updateFormValue("pinCode")}
              />
            </div>
          </Panel>
        </div>

        <Panel
          className="mx-1 mt-4"
          icon={
            <FaRegCommentDots
              className="h-4 w-4 flex-none"
              aria-hidden="true"
            />
          }
          title="Comments"
          titleClassName="text-[#06b6d4]"
        >
          <textarea
            className="min-h-[62px] w-full rounded-md border border-[#d8dee6] bg-white px-3 py-2 text-base outline-none transition placeholder:text-[#4b5563] focus:border-[#0d73ff] focus:shadow-[0_0_0_4px_rgba(13,115,255,0.18)]"
            placeholder="Add call notes or customer comments"
            value={formValues.comments}
            onChange={updateFormValue("comments")}
          />
        </Panel>

        <div className="mt-4 flex justify-start">
          <button
            className="inline-flex items-center gap-2 rounded-md bg-[#1773f8] px-4 py-2 text-base font-medium text-white shadow transition hover:bg-[#0d63dc] focus-visible:bg-[#0d63dc] disabled:cursor-not-allowed disabled:bg-[#8bbcf8]"
            disabled={isSaving}
            type="submit"
          >
            <FaSave className="h-4 w-4 flex-none" aria-hidden="true" />
            {isSaving ? "Saving..." : "Save Entry"}
          </button>
        </div>
      </form>
    </ActionPageLayout>
  );
}

export default InboundCallEntryPage;
